import { splitRideSegments } from "./ride-segments";
import type { RecordingDevice } from "../../../renderer/src/recording-device";
import type { RecordedRideSource } from '../../../renderer/src/ride-source';

/** One normalized ride sample in longitude, latitude, elevation, and UTC time. */
export interface RidePoint {
  readonly longitude: number;
  readonly latitude: number;
  readonly elevationMeters: number;
  readonly recordedAt: string;
  /** Starts a disconnected segment; no edge joins it to the preceding point. */
  readonly segmentStart?: boolean;
}

/** Normalized ride displayed by the visual preview. */
export interface Ride {
  readonly id: string;
  /** Content hash used only to remember this source track's name in the browser. */
  readonly sourceFingerprint?: string;
  readonly sourceFileName?: string;
  readonly recordingDevice?: RecordingDevice;
  /** Device-reported active time, excluding pauses; original GPS timestamps stay intact. */
  readonly timerDurationSeconds?: number | undefined;
  readonly metrics?: RideMetrics;
  readonly title: string;
  readonly region: string;
  readonly source: RecordedRideSource | "synthetic";
  readonly points: readonly RidePoint[];
  /** Full imported samples retained when interactive display points are reduced. */
  readonly sourcePoints?: readonly RidePoint[];
}

/** Aggregate values computed from normalized ride points. */
export interface RideMetrics {
  readonly distanceKilometers: number;
  readonly ascentMeters: number;
  readonly descentMeters: number;
  readonly durationSeconds: number;
  readonly averageKilometersPerHour: number;
  readonly minimumElevationMeters: number;
  readonly maximumElevationMeters: number;
}

/** Route slice and current point for a bounded playback position. */
export interface RideProgress {
  readonly currentPoint: RidePoint;
  readonly traveledPoints: readonly RidePoint[];
  readonly pointIndex: number;
  readonly fraction: number;
}

/** Distance-indexed evaluator shared by map, telemetry, and elevation views. */
export interface RideProgressPlan {
  readonly distanceFractions: readonly number[];
  readonly totalDistanceKilometers: number;
  at(fraction: number): RideProgress;
  pointAt(fraction: number): RidePoint;
  /** Samples near a position without crossing its segment boundary. */
  pointNear(fraction: number, reference: number): RidePoint;
}

interface RideProgressPosition {
  readonly currentPoint: RidePoint;
  readonly fraction: number;
  readonly pointIndex: number;
}

const EARTH_RADIUS_KILOMETERS = 6_371.0088;
const RIDE_PROGRESS_PLAN_CACHE = new WeakMap<readonly RidePoint[], RideProgressPlan>();

/**
 * Computes distance, duration, elevation, and average-speed statistics.
 *
 * @param points - Time-ordered normalized ride points.
 * @param timerDurationSeconds - Optional device timer total, excluding paused time.
 * @returns Aggregate metrics for the entire ride.
 * @throws RangeError when fewer than two points are supplied or timestamps regress.
 * @throws TypeError when a coordinate, elevation, or timestamp is invalid.
 */
export function calculateRideMetrics(points: readonly RidePoint[], timerDurationSeconds?: number): RideMetrics {
  validateRidePoints(points);
  if (timerDurationSeconds !== undefined && (!Number.isFinite(timerDurationSeconds) || timerDurationSeconds < 0)) {
    throw new TypeError('The trace timer duration must be finite and non-negative.');
  }
  let distanceKilometers = 0;
  let ascentMeters = 0;
  let descentMeters = 0;
  let minimumElevationMeters = points[0]!.elevationMeters;
  let maximumElevationMeters = points[0]!.elevationMeters;

  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1]!;
    const current = points[index]!;
    distanceKilometers += distanceBetween(previous, current);
    const elevationDelta = current.segmentStart ? 0 : current.elevationMeters - previous.elevationMeters;
    if (elevationDelta > 0) {
      ascentMeters += elevationDelta;
    } else {
      descentMeters += Math.abs(elevationDelta);
    }
    minimumElevationMeters = Math.min(minimumElevationMeters, current.elevationMeters);
    maximumElevationMeters = Math.max(maximumElevationMeters, current.elevationMeters);
  }

  const start = Date.parse(points[0]!.recordedAt);
  const end = Date.parse(points.at(-1)!.recordedAt);
  const durationSeconds = timerDurationSeconds ?? (end - start) / 1_000;
  return {
    distanceKilometers,
    ascentMeters,
    descentMeters,
    durationSeconds,
    averageKilometersPerHour: durationSeconds === 0
      ? 0
      : distanceKilometers / (durationSeconds / 3_600),
    minimumElevationMeters,
    maximumElevationMeters,
  };
}

/**
 * Compiles validated ride points into a distance-indexed playback evaluator.
 *
 * @param points - Valid normalized ride points.
 * @returns Reusable evaluator and normalized distance positions for every sample.
 * @throws RangeError when fewer than two points are supplied.
 * @throws TypeError when point data is invalid.
 */
export function compileRideProgress(points: readonly RidePoint[]): RideProgressPlan {
  const cached = RIDE_PROGRESS_PLAN_CACHE.get(points);
  if (cached !== undefined) {
    return cached;
  }
  validateRidePoints(points);
  const cumulativeDistances = [0];
  for (let index = 1; index < points.length; index += 1) {
    cumulativeDistances.push(
      cumulativeDistances[index - 1]! + distanceBetween(points[index - 1]!, points[index]!),
    );
  }
  const totalDistanceKilometers = cumulativeDistances.at(-1)!;
  const distanceFractions = cumulativeDistances.map((distance, index) => (
    totalDistanceKilometers === 0
      ? index / (points.length - 1)
      : distance / totalDistanceKilometers
  ));
  const positionAt = (fraction: number): RideProgressPosition => resolveProgressPosition(
    points,
    cumulativeDistances,
    totalDistanceKilometers,
    fraction,
  );
  const segments = splitRideSegments(points);
  const plan: RideProgressPlan = {
    at(fraction) {
      const position = positionAt(fraction);
      return {
        ...position,
        traveledPoints: [
          ...points.slice(0, position.pointIndex + 1),
          position.currentPoint,
        ],
      };
    },
    distanceFractions,
    pointAt(fraction) {
      return positionAt(fraction).currentPoint;
    },
    pointNear(fraction, reference) {
      const position = positionAt(reference);
      const segment = segments.findLast(item => item.startIndex <= position.pointIndex)!;
      const endIndex = segment.startIndex + segment.points.length - 1;
      if (fraction <= distanceFractions[segment.startIndex]!) return segment.points[0]!;
      if (fraction >= distanceFractions[endIndex]!) return segment.points.at(-1)!;
      return positionAt(fraction).currentPoint;
    },
    totalDistanceKilometers,
  };
  RIDE_PROGRESS_PLAN_CACHE.set(points, plan);
  return plan;
}

/**
 * Resolves a continuous distance-based position across discrete ride samples.
 *
 * @param points - Valid normalized ride points.
 * @param fraction - Requested route-distance position, clamped to zero through one.
 * @returns Traveled route, interpolated current point, and source segment index.
 * @throws RangeError when fewer than two points are supplied.
 * @throws TypeError when point data or fraction is invalid.
 */
export function rideProgressAt(points: readonly RidePoint[], fraction: number): RideProgress {
  return compileRideProgress(points).at(fraction);
}

/**
 * Formats a duration as an hours, minutes, and seconds clock.
 *
 * @param seconds - Non-negative duration in seconds.
 * @returns Zero-padded clock string.
 */
export function formatDuration(seconds: number): string {
  const bounded = Math.max(0, Math.round(seconds));
  const hours = Math.floor(bounded / 3_600);
  const minutes = Math.floor((bounded % 3_600) / 60);
  const remainingSeconds = bounded % 60;
  return [hours, minutes, remainingSeconds]
    .map((part) => part.toString().padStart(2, "0"))
    .join(":");
}

/**
 * Validates normalized points and monotonic timestamps.
 *
 * @param points - Candidate ride-point collection.
 * @throws RangeError when the collection is too short or timestamps regress.
 * @throws TypeError when point data is invalid.
 */
function validateRidePoints(points: readonly RidePoint[]): void {
  if (!Array.isArray(points)) {
    throw new TypeError("Trace points must be an array.");
  }
  if (points.length < 2) {
    throw new RangeError("A trace requires at least two points.");
  }
  let previousTime = -Infinity;
  for (const point of points) {
    if (
      (point.segmentStart !== undefined && typeof point.segmentStart !== "boolean")
      || !Number.isFinite(point.longitude)
      || !Number.isFinite(point.latitude)
      || !Number.isFinite(point.elevationMeters)
      || Math.abs(point.longitude) > 180
      || Math.abs(point.latitude) > 90
    ) {
      throw new TypeError("Trace point coordinates and elevation must be finite and bounded.");
    }
    const timestamp = Date.parse(point.recordedAt);
    if (!Number.isFinite(timestamp)) {
      throw new TypeError("Trace point timestamps must be valid ISO dates.");
    }
    if (timestamp < previousTime) {
      throw new RangeError("Trace point timestamps must not regress.");
    }
    previousTime = timestamp;
  }
}

/**
 * Calculates great-circle distance between two normalized points.
 *
 * @param first - Starting point.
 * @param second - Ending point.
 * @returns Distance in kilometers.
 */
function distanceBetween(first: RidePoint, second: RidePoint): number {
  if (second.segmentStart) return 0;
  const latitudeDelta = degreesToRadians(second.latitude - first.latitude);
  const longitudeDelta = degreesToRadians(second.longitude - first.longitude);
  const firstLatitude = degreesToRadians(first.latitude);
  const secondLatitude = degreesToRadians(second.latitude);
  const haversine = Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(firstLatitude) * Math.cos(secondLatitude) * Math.sin(longitudeDelta / 2) ** 2;
  return 2 * EARTH_RADIUS_KILOMETERS * Math.asin(Math.sqrt(haversine));
}

/** Finds the first cumulative-distance sample greater than or equal to a target. */
function lowerBound(cumulativeDistances: readonly number[], target: number): number {
  let low = 1;
  let high = cumulativeDistances.length - 1;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (cumulativeDistances[middle]! < target) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }
  return low;
}

/** Resolves one route position without allocating the traveled polyline. */
function resolveProgressPosition(
  points: readonly RidePoint[],
  cumulativeDistances: readonly number[],
  totalDistanceKilometers: number,
  fraction: number,
): RideProgressPosition {
  if (!Number.isFinite(fraction)) {
    throw new TypeError("Trace progress must be a finite number.");
  }
  const boundedFraction = Math.min(1, Math.max(0, fraction));
  if (boundedFraction === 0) {
    return { currentPoint: points[0]!, fraction: 0, pointIndex: 0 };
  }
  if (boundedFraction === 1) {
    return {
      currentPoint: points.at(-1)!,
      fraction: 1,
      pointIndex: points.length - 2,
    };
  }
  if (totalDistanceKilometers === 0) {
    return positionAtPointIndex(points, boundedFraction);
  }
  const targetDistance = boundedFraction * totalDistanceKilometers;
  let endIndex = lowerBound(cumulativeDistances, targetDistance);
  // At a discontinuity, use the next segment at the same traveled distance.
  while (endIndex < points.length - 1 && cumulativeDistances[endIndex] === targetDistance
    && cumulativeDistances[endIndex + 1] === targetDistance) endIndex += 1;
  if (points[endIndex]?.segmentStart && endIndex < points.length - 1) endIndex += 1;
  const pointIndex = Math.min(points.length - 2, Math.max(0, endIndex - 1));
  const segmentStart = cumulativeDistances[pointIndex]!;
  const segmentEnd = cumulativeDistances[pointIndex + 1]!;
  const segmentDistance = segmentEnd - segmentStart;
  const localFraction = segmentDistance === 0
    ? 0
    : (targetDistance - segmentStart) / segmentDistance;
  return positionBetween(points, pointIndex, localFraction, boundedFraction);
}

/** Resolves index-based position for a route whose geographic distance is zero. */
function positionAtPointIndex(
  points: readonly RidePoint[],
  fraction: number,
): RideProgressPosition {
  const scaledIndex = fraction * (points.length - 1);
  const pointIndex = Math.min(points.length - 2, Math.floor(scaledIndex));
  return positionBetween(points, pointIndex, scaledIndex - pointIndex, fraction);
}

/** Interpolates one route segment without copying preceding route points. */
function positionBetween(
  points: readonly RidePoint[],
  pointIndex: number,
  localFraction: number,
  fraction: number,
): RideProgressPosition {
  const start = points[pointIndex]!;
  const end = points[pointIndex + 1]!;
  if (end.segmentStart) {
    return { currentPoint: localFraction < 1 ? start : end, pointIndex, fraction };
  }
  const currentPoint: RidePoint = {
    longitude: interpolateLongitude(start.longitude, end.longitude, localFraction),
    latitude: interpolate(start.latitude, end.latitude, localFraction),
    elevationMeters: interpolate(start.elevationMeters, end.elevationMeters, localFraction),
    recordedAt: new Date(
      interpolate(Date.parse(start.recordedAt), Date.parse(end.recordedAt), localFraction),
    ).toISOString(),
  };
  return { currentPoint, pointIndex, fraction };
}

/** @param degrees - Angle in degrees. @returns The same angle in radians. */
function degreesToRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Interpolates between numeric endpoints.
 *
 * @param start - Initial numeric value.
 * @param end - Final numeric value.
 * @param fraction - Local interpolation fraction.
 * @returns Interpolated value.
 */
function interpolate(start: number, end: number, fraction: number): number {
  return start + (end - start) * fraction;
}

/** Interpolates the short arc across the dateline and returns a valid longitude. */
function interpolateLongitude(start: number, end: number, fraction: number): number {
  let delta = end - start;
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  const longitude = start + delta * fraction;
  if (longitude > 180) return longitude - 360;
  if (longitude < -180) return longitude + 360;
  return longitude;
}
